import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaceShape, NAMED_FACE_SHAPES, type FaceShape as FaceShapeValue } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { apiErrorMessage } from '@/services/apiClient';
import { useRecordFaceAssessmentMutation } from '../api/face.api';
import { FaceShapeGlyph } from './FaceShapeGlyph';

/** Every choice, in the order the taxonomy is usually taught, with the two non-answers last. */
const CHOICES: FaceShapeValue[] = [...NAMED_FACE_SHAPES, FaceShape.MIXED, FaceShape.UNKNOWN];

/**
 * An optician recording the face shape they decided on.
 *
 * Nothing is pre-selected. There is no camera and therefore no suggestion to agree with, and a
 * pre-selected shape would turn a judgement into a default somebody forgot to change.
 */
export function SetFaceShapeDialog({
  customerId,
  isOpen,
  onClose,
  onRecorded,
}: {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called once the shape is saved, before the dialog closes, for a screen that depends on it. */
  onRecorded?: () => void;
}) {
  const { t } = useTranslation();
  const record = useRecordFaceAssessmentMutation(customerId);

  const [shape, setShape] = useState<FaceShapeValue | null>(null);
  const [components, setComponents] = useState<FaceShapeValue[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setShape(null);
    setComponents([]);
    setError(null);
  }, [isOpen]);

  const isMixed = shape === FaceShape.MIXED;
  const ready = shape !== null && (!isMixed || components.length === 2);

  const toggleComponent = (choice: FaceShapeValue) =>
    setComponents((current) =>
      current.includes(choice)
        ? current.filter((c) => c !== choice)
        : // Exactly two: picking a third replaces the older of the pair rather than silently doing nothing.
          [...current, choice].slice(-2),
    );

  const submit = () => {
    if (!shape) return;
    setError(null);
    record.mutate(
      isMixed
        ? { confirmedShape: shape, componentA: components[0], componentB: components[1] }
        : { confirmedShape: shape },
      {
        onSuccess: () => {
          onRecorded?.();
          onClose();
        },
        onError: (caught) => setError(apiErrorMessage(caught) ?? t('face.errors.record')),
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('face.set.title')}
      description={t('face.set.description')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!ready} isLoading={record.isPending}>
            {t('face.set.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <fieldset>
          <legend className="text-muted mb-2 text-xs font-medium tracking-wide uppercase">
            {t('face.set.chooseShape')}
          </legend>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {CHOICES.map((choice) => (
              <ShapeButton
                key={choice}
                shape={choice}
                isSelected={shape === choice}
                onSelect={() => {
                  setShape(choice);
                  if (choice !== FaceShape.MIXED) setComponents([]);
                }}
              />
            ))}
          </div>
        </fieldset>

        {isMixed ? (
          <fieldset>
            <legend className="text-muted mb-2 text-xs font-medium tracking-wide uppercase">
              {t('face.set.chooseComponents')}
            </legend>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {NAMED_FACE_SHAPES.map((choice) => (
                <ShapeButton
                  key={choice}
                  shape={choice}
                  isSelected={components.includes(choice)}
                  onSelect={() => toggleComponent(choice)}
                />
              ))}
            </div>
            <p className="text-muted mt-2 text-xs">{t('face.set.componentsHint')}</p>
          </fieldset>
        ) : null}

        <p className="text-muted text-xs">{t('face.set.note')}</p>
      </div>
    </Modal>
  );
}

function ShapeButton({
  shape,
  isSelected,
  onSelect,
}: {
  shape: FaceShapeValue;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      className={`flex flex-col items-center gap-1.5 rounded-lg border p-2.5 text-center transition-colors ${
        isSelected
          ? 'border-brand-600 bg-brand-50 text-brand-700'
          : 'border-line text-muted hover:border-brand-300 hover:text-ink bg-white'
      }`}
    >
      <FaceShapeGlyph shape={shape} className="h-12 w-9" />
      <span className="text-xs font-medium">{t(`face.shapes.${shape}`)}</span>
    </button>
  );
}
